import type {
  HandicapConfigJson,
  PieceJson,
  PieceKindId,
  RulesetId,
  SideId,
} from '@xiangqi/game-core';
import type { HandicapConfig } from '@xiangqi/protocol';

export const FILES = 9;
export const RANKS = 10;
export const SQUARES = 90;

export function file(sq: number): number {
  return sq % 9;
}

export function rank(sq: number): number {
  return Math.floor(sq / 9);
}

export function square(f: number, r: number): number | null {
  if (f >= 0 && f < FILES && r >= 0 && r < RANKS) {
    return r * 9 + f;
  }
  return null;
}

export function inPalace(sq: number, side: SideId): boolean {
  const f = file(sq);
  const r = rank(sq);
  if (f < 3 || f > 5) return false;
  return side === 'red' ? r >= 0 && r <= 2 : r >= 7 && r <= 9;
}

export function onOwnSide(sq: number, side: SideId): boolean {
  const r = rank(sq);
  return side === 'red' ? r <= 4 : r >= 5;
}

export function pieceMovesAs(piece: PieceJson): PieceKindId | undefined {
  if (piece.covered) {
    return piece.square_kind;
  }
  return piece.kind;
}

export function isUnbound(ruleset: RulesetId, piece: PieceJson): boolean {
  return ruleset !== 'standard' && !piece.covered;
}

export function isRoyalPiece(
  ruleset: RulesetId,
  piece: { side: SideId; kind?: PieceKindId | null; covered?: boolean },
  handicap?: HandicapConfigJson | HandicapConfig | null,
): boolean {
  if (piece.covered || !piece.kind) return false;
  const royalSide =
    handicap && 'royal_side' in handicap && handicap.royal_side
      ? handicap.royal_side
      : handicap && 'type' in handicap && handicap.type === 'asym_royal'
        ? handicap.giver_side
        : undefined;

  if (royalSide && piece.side !== royalSide) {
    return false;
  }
  if (ruleset === 'blind_si_chua') return piece.kind === 'advisor';
  if (ruleset === 'blind_tuong_vuong') return piece.kind === 'elephant';
  if (ruleset === 'blind_royal') return piece.kind === 'advisor' || piece.kind === 'elephant';
  return false;
}

function genGeneral(
  from: number,
  side: SideId,
  boardSquares: (PieceJson | null)[],
  out: Set<number>,
): void {
  const f = file(from);
  const r = rank(from);
  const ortho = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ] as const;
  for (const [df, dr] of ortho) {
    const to = square(f + df, r + dr);
    if (to !== null && inPalace(to, side)) {
      out.add(to);
    }
  }
  // Flying general (opposite general along same file or opposing palace squares on same file)
  const oppSide: SideId = side === 'red' ? 'black' : 'red';
  for (let r2 = 0; r2 < RANKS; r2++) {
    const sq2 = square(f, r2);
    if (sq2 !== null && sq2 !== from) {
      if (inPalace(sq2, oppSide)) {
        out.add(sq2);
      } else {
        const p = boardSquares[sq2];
        if (p && p.side === oppSide && !p.covered && p.kind === 'general') {
          out.add(sq2);
        }
      }
    }
  }
}

function genAdvisor(
  from: number,
  side: SideId,
  unbound: boolean,
  out: Set<number>,
): void {
  const f = file(from);
  const r = rank(from);
  const diag = [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ] as const;
  for (const [df, dr] of diag) {
    const to = square(f + df, r + dr);
    if (to !== null && (unbound || inPalace(to, side))) {
      out.add(to);
    }
  }
}

function genElephant(
  from: number,
  side: SideId,
  mayCross: boolean,
  out: Set<number>,
): void {
  const f = file(from);
  const r = rank(from);
  const diag = [
    [2, 2],
    [2, -2],
    [-2, 2],
    [-2, -2],
  ] as const;
  for (const [df, dr] of diag) {
    const to = square(f + df, r + dr);
    // Eye block is intentionally ignored for premove!
    if (to !== null && (mayCross || onOwnSide(to, side))) {
      out.add(to);
    }
  }
}

function genHorse(from: number, out: Set<number>): void {
  const f = file(from);
  const r = rank(from);
  const offsets = [
    [1, 2],
    [-1, 2],
    [1, -2],
    [-1, -2],
    [2, 1],
    [-2, 1],
    [2, -1],
    [-2, -1],
  ] as const;
  for (const [df, dr] of offsets) {
    const to = square(f + df, r + dr);
    // Leg block is intentionally ignored for premove!
    if (to !== null) {
      out.add(to);
    }
  }
}

function genChariot(from: number, out: Set<number>): void {
  const f = file(from);
  const r = rank(from);
  for (let f2 = 0; f2 < FILES; f2++) {
    if (f2 !== f) {
      const to = square(f2, r);
      if (to !== null) out.add(to);
    }
  }
  for (let r2 = 0; r2 < RANKS; r2++) {
    if (r2 !== r) {
      const to = square(f, r2);
      if (to !== null) out.add(to);
    }
  }
}

function genCannon(from: number, out: Set<number>): void {
  // Cannon can move to any square on file/rank (either quiet move or capture over screen)
  genChariot(from, out);
}

function genSoldier(from: number, side: SideId, out: Set<number>): void {
  const f = file(from);
  const r = rank(from);
  const forward = side === 'red' ? 1 : -1;
  const forwardSq = square(f, r + forward);
  if (forwardSq !== null) {
    out.add(forwardSq);
  }
  if (!onOwnSide(from, side)) {
    for (const df of [-1, 1]) {
      const sideSq = square(f + df, r);
      if (sideSq !== null) {
        out.add(sideSq);
      }
    }
  }
}

/**
 * Calculates all premove destination squares for a given piece.
 * Ignores obstacles (blocked horse legs, elephant eye, chariot/cannon screens),
 * pins, and in-check conditions, because board state can change on opponent turn.
 * Allows targeting empty squares as well as opponent pieces for captures.
 * Filters out squares containing friendly pieces.
 */
export function getPremoveDestinations(
  from: number,
  boardSquares: (PieceJson | null)[],
  ruleset: RulesetId = 'standard',
  handicap?: HandicapConfigJson | HandicapConfig | null,
): number[] {
  const piece = boardSquares[from];
  if (!piece) return [];

  const side = piece.side;
  const out = new Set<number>();

  if (isRoyalPiece(ruleset, piece, handicap)) {
    if (piece.kind === 'advisor') {
      genAdvisor(from, side, true, out);
      genHorse(from, out);
      genSoldier(from, side, out);
    } else if (piece.kind === 'elephant') {
      genElephant(from, side, true, out);
      genHorse(from, out);
      genSoldier(from, side, out);
    }
  } else {
    const pattern = pieceMovesAs(piece);
    if (pattern) {
      const unbound = isUnbound(ruleset, piece);
      switch (pattern) {
        case 'general':
          genGeneral(from, side, boardSquares, out);
          break;
        case 'advisor':
          genAdvisor(from, side, unbound, out);
          break;
        case 'elephant':
          genElephant(from, side, unbound, out);
          break;
        case 'horse':
          genHorse(from, out);
          break;
        case 'chariot':
          genChariot(from, out);
          break;
        case 'cannon':
          genCannon(from, out);
          break;
        case 'soldier':
          genSoldier(from, side, out);
          break;
      }
    }
  }

  // Filter out only the starting square.
  // Empty squares, opponent pieces (captures), and friendly pieces
  // (for trades/recaptures when the friendly piece is captured or moves) are preserved!
  return Array.from(out).filter((to) => to !== from);
}

export const DOUBLE_TAP_THRESHOLD_MS = 400;

/**
 * Checks whether a tap on the board area should trigger premove cancellation.
 * Quick double tap (two taps within thresholdMs) on any area (piece or board)
 * cancels premove, unless the second tap is executing a valid move.
 */
export function shouldCancelPremoveOnTap(
  prevTapTime: number | null,
  now: number,
  isMove: boolean,
  thresholdMs: number = DOUBLE_TAP_THRESHOLD_MS,
): { isDoubleTap: boolean; cancelPremove: boolean } {
  if (prevTapTime !== null && now - prevTapTime < thresholdMs) {
    if (isMove) {
      return { isDoubleTap: true, cancelPremove: false };
    }
    return { isDoubleTap: true, cancelPremove: true };
  }
  return { isDoubleTap: false, cancelPremove: false };
}
